import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLocation, useNavigate } from 'react-router-dom';
import '../Css/AddProduct.css';
import { FaArrowLeft } from 'react-icons/fa';
import BASEURL from '../API/Config';

// ✅ Reusable Input Component
const CustomInput = ({
  type,
  label,
  name,
  value,
  onChange,
  options = [],
  disabled = false,
  error,
}) => (
  <div className="form-group">
    <label className="form-label small mb-1">{label}</label>
    {type === 'select' ? (
      <select
        name={name}
        className="form-control form-control-sm"
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option._id} value={option._id}>
            {option.title}
          </option>
        ))}
      </select>
    ) : type === 'unit' ? (
      <select
        name={name}
        className="form-control form-control-sm"
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        <option value="">Select Unit</option>
        {options.map((u) => (
          <option key={u._id} value={u._id}>{u.title}</option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        name={name}
        className="form-control form-control-sm"
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    )}
    {error && <span className="text-danger small">{error}</span>}
  </div>
);

const ProductForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const { product, isEdit } = location.state || {};

  const [formData, setFormData] = useState({
    title: product?.title || '',
    description: product?.description || '',
    CurrentPrice: product?.CurrentPrice || '',
    offerPrice: product?.offerPrice || '',
    Category: product?.Category?._id || product?.Category || '',
    subCategory: product?.subCategory?._id || product?.subCategory || '',
    quantity: product?.quantity || '',
    images: [],
    existingImages: product?.images || [],
    unit: product?.unit?._id || product?.unit || '',
    gst: product?.gst || '',
    remarks: product?.remarks || '',
    variants: (product?.variants && product.variants.length > 0) 
      ? product.variants.map(v => ({
          ...v,
          unit: v.unit?._id || v.unit || '',
          measurementUnit: v.measurementUnit || v.MeasurementUnit || '',
          CurrentPrice: v.CurrentPrice || '',
          offerPrice: v.offerPrice || '',
          quantity: v.quantity || ''
        }))
      : [{ unit: '', measurementUnit: '', CurrentPrice: '', offerPrice: '', quantity: '' }],
  });

  const [previewImages, setPreviewImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);

  // ✅ Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${BASEURL}/pcategory`);
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch {
        Swal.fire('Error', 'Failed to load categories.', 'error');
      }
    };
    fetchCategories();
  }, []);

  // ✅ Fetch Units
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await fetch(`${BASEURL}/units`);
        if (res.ok) {
          const data = await res.json();
          setUnits(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch units:", err);
      }
    };
    fetchUnits();
  }, []);

  // ✅ Fetch Subcategories on Category Change
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!formData.Category) return;
      try {
        const res = await fetch(`${BASEURL}/pcategory/cat&sub/${formData.Category}`);
        const data = await res.json();
        setSubCategories(
          Array.isArray(data.category?.subcategories) ? data.category.subcategories : []
        );
      } catch {
        Swal.fire('Error', 'Failed to load subcategories.', 'error');
      }
    };
    fetchSubCategories();
  }, [formData.Category]);

  // ✅ File Handling
  const handleFiles = (fileArray) => {
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const totalImages = formData.existingImages.length + previewImages.length + fileArray.length;

    if (totalImages > 5) {
      Swal.fire('Limit Exceeded', 'You can upload a maximum of 5 images.', 'warning');
      return;
    }

    for (let file of fileArray) {
      if (!allowedTypes.includes(file.type)) {
        Swal.fire('Invalid File', 'Only PNG, JPG, or JPEG files are allowed.', 'warning');
        return;
      }
    }

    const newPreviews = fileArray.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setFormData((prev) => ({ ...prev, images: [...prev.images, ...fileArray] }));
    setPreviewImages((prev) => [...prev, ...newPreviews]);
  };

  // ✅ Change Handler
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      const fileArray = Array.from(files);
      handleFiles(fileArray);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, { unit: '', measurementUnit: '', CurrentPrice: '', offerPrice: '', quantity: '' }],
    }));
  };

  const handleVariantChange = (index, e) => {
    const { name, value } = e.target;
    const newVariants = [...formData.variants];
    newVariants[index][name] = value;
    setFormData((prev) => ({ ...prev, variants: newVariants }));
  };

  const removeVariant = (index) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  // ✅ Drag & Drop Events
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const fileArray = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    if (fileArray.length > 0) handleFiles(fileArray);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const totalUploaded = formData.existingImages.length + previewImages.length;

  // ✅ Remove Images
  const removeExistingImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, i) => i !== index),
    }));
  };
  const removePreviewImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = 'Title is required.';
    if (!formData.description) newErrors.description = 'Description is required.';
    if (!formData.Category) newErrors.Category = 'Category is required.';
    if (!formData.subCategory) newErrors.subCategory = 'Subcategory is required.';

    if (formData.variants.length === 0) {
      newErrors.variants = 'Please add at least one product variant (Unit, Price, Quantity).';
    } else {
      // Validate variants
      formData.variants.forEach((v, index) => {
        if (!v.unit || !v.measurementUnit || !v.CurrentPrice || v.CurrentPrice <= 0 || !v.quantity || v.quantity <= 0) {
          newErrors.variants = 'Please fill all variant details correctly (Unit, Measurement Unit, Price, Quantity).';
        }
      });
    }

    if (formData.existingImages.length + formData.images.length === 0)
      newErrors.images = 'At least one image is required.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const url = isEdit
      ? `${BASEURL}/Products/productup/${product._id}`
      : `${BASEURL}/Products/create-product`;

    const token = localStorage.getItem('token');
    const formDataToSubmit = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'images') {
        value.forEach((file) => formDataToSubmit.append('images', file));
      } else if (key === 'existingImages') {
        value.forEach((img) => formDataToSubmit.append('existingImages', img.url));
      } else if (key === 'variants') {
        formDataToSubmit.append('variants', JSON.stringify(value));
      } else {
        formDataToSubmit.append(key, value);
      }
    });

    try {
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataToSubmit,
      });

      if (!res.ok) throw new Error();

      Swal.fire('Success', `Product ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
      navigate('/admin/product-list');
    } catch {
      Swal.fire('Error', 'Submission failed. Please try again.', 'error');
    }
  };

  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <span onClick={() => navigate(-1)} style={{ cursor: 'pointer', color: '#333' }}>
              <FaArrowLeft />
            </span>
            <h5 className="mb-0">{isEdit ? 'Edit Product' : 'Create Product'}</h5>
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate('/admin/product-list')}
          >
            Product List
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <CustomInput
                type="text"
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={errors.title}
              />
            </div>
            <div className="col-md-6">
              <CustomInput
                type="text"
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                error={errors.description}
              />
            </div>
            <div className="col-md-6">
              <CustomInput
                type="select"
                label="Category"
                name="Category"
                value={formData.Category}
                onChange={handleChange}
                options={categories}
                error={errors.Category}
              />
            </div>
            <div className="col-md-6">
              <CustomInput
                type="select"
                label="Subcategory"
                name="subCategory"
                value={formData.subCategory}
                onChange={handleChange}
                options={subCategories}
                disabled={!formData.Category}
                error={errors.subCategory}
              />
            </div>
            <div className="col-md-3">
              <CustomInput
                type="number"
                label="GST (Optional)"
                name="gst"
                value={formData.gst}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-8">
              <CustomInput
                type="text"
                label="Remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Product Variants (Required)</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleAddVariant}
                >
                  + Add Variant
                </button>
              </div>
              {errors.variants && (
                <div className="alert alert-danger py-1 small mb-2">
                  {errors.variants}
                </div>
              )}
              {formData.variants.length > 0 && (
                <div className="table-responsive border rounded bg-light p-2 mb-3">
                  <table className="table table-sm table-hover mb-0 align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: '20%' }}>Unit</th>
                        <th style={{ width: '20%' }}>Measurement Unit</th>
                        <th style={{ width: '15%' }}>Current Price</th>
                        <th style={{ width: '15%' }}>Offer Price</th>
                        <th style={{ width: '15%' }}>Quantity</th>
                        <th style={{ width: '15%' }} className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.variants.map((variant, index) => (
                        <tr key={index}>
                          <td className="p-2">
                            <select
                              name="unit"
                              className="form-select form-select-sm"
                              value={variant.unit}
                              onChange={(e) => handleVariantChange(index, e)}
                            >
                              <option value="">Select Unit</option>
                              {units.map((u) => (
                                <option key={u._id} value={u._id}>{u.title}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              name="measurementUnit"
                              className="form-control form-control-sm"
                              placeholder="e.g. 20kg"
                              value={variant.measurementUnit}
                              onChange={(e) => handleVariantChange(index, e)}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              name="CurrentPrice"
                              className="form-control form-control-sm"
                              placeholder="Price"
                              value={variant.CurrentPrice}
                              onChange={(e) => handleVariantChange(index, e)}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              name="offerPrice"
                              className="form-control form-control-sm"
                              placeholder="Offer"
                              value={variant.offerPrice}
                              onChange={(e) => handleVariantChange(index, e)}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              name="quantity"
                              className="form-control form-control-sm"
                              placeholder="Qty"
                              value={variant.quantity}
                              onChange={(e) => handleVariantChange(index, e)}
                            />
                          </td>
                          <td className="p-2 text-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeVariant(index)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="col-md-12">
              <label className="form-label small mb-1">Upload Images (Max 5)</label>
              <div
                className={`drag-drop-zone ${isDragOver ? 'drag-over' : ''} ${
                  totalUploaded >= 5 ? 'disabled' : ''
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => totalUploaded < 5 && fileInputRef.current.click()}
                style={{ cursor: totalUploaded >= 5 ? 'not-allowed' : 'pointer' }}
              >
                <input
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg"
                  ref={fileInputRef}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                />
                <p>Drag & drop images here or click to select</p>
                <small>PNG, JPG, JPEG only. Max 5 images.</small>
              </div>
              <small className="text-muted">
                You can upload {5 - totalUploaded} more image(s).
              </small>
              {errors.images && <div className="text-danger small">{errors.images}</div>}
            </div>
          </div>

          {(formData.existingImages.length > 0 || previewImages.length > 0) && (
            <div className="image-preview mt-3">
              <h6>Product Images (Click ❌ to remove)</h6>
              <div className="d-flex flex-wrap gap-2">
                {formData.existingImages.map((image, index) => (
                  <div key={`existing-${index}`} className="position-relative">
                    <img
                      src={`${BASEURL}${image.url}`}
                      className="img-thumbnail"
                      style={{ width: 100, height: 100 }}
                      alt="Existing"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      onClick={() => removeExistingImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {previewImages.map((img, index) => (
                  <div key={`preview-${index}`} className="position-relative">
                    <img
                      src={img.url}
                      className="img-thumbnail"
                      style={{ width: 100, height: 100 }}
                      alt="Preview"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      onClick={() => removePreviewImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-end mt-4">
            <button type="submit" className="btn btn-success btn-sm px-4">
              {isEdit ? 'Update' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
